"use client";

import { ArrowRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CoreConsultancyPillars() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const pillars = [{
    number: "01",
    tag: "Rate & Yield Management",
    title: "Daily Pricing & Yield Management",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_yielding.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_yielding.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_yielding.png-wvc-sizes",
      alt: "Hotel revenue management rate updates on laptop"
    },
    points: ["Live competitor rate tracking and pickup pace monitoring", "Daily ADR adjustments based on real market compression", "Event surge pricing and lead-time restriction rules", "Minimum length of stay (MLOS) and stop-sell control"],
    link: "/ServicesPage",
    linkText: "Explore Dynamic Pricing"
  }, {
    number: "02",
    tag: "Visibility & Distribution",
    title: "OTA Channel Management",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_channel_optimization.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_channel_optimization.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_channel_optimization.webp-wvc-sizes",
      alt: "OTA distribution dashboard and channel management"
    },
    points: ["Ranking audits on Booking.com, Agoda, MakeMyTrip, and Expedia", "Content score fixes, room tagging, and photo curation", "Opting out of unprofitable OTA discount schemes and markdowns", "Daily rate parity audits to stop OTAs from undercutting your direct rate"],
    link: "/ServicesPage",
    linkText: "Explore Channel Management"
  }, {
    number: "03",
    tag: "Direct Acquisition",
    title: "Direct Booking Growth",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_loyalty.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_loyalty.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_loyalty.png-wvc-sizes",
      alt: "Direct booking engine interface on luxury hotel tablet"
    },
    points: ["Frictionless booking engine flow and fast mobile checkout", "Google Hotel Ads and free booking link setup", "Direct guest contact capture for repeat stay campaigns", "Value-add perks and fenced member rates that protect parity"],
    link: "/ServicesPage",
    linkText: "Explore Direct Growth"
  }];
  return <section data-section-id="241" id="core-consultancy-pillars" className="bg-background text-foreground py-20 md:py-28 lg:py-32 border-b border-border/60">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header Row: Left H2 + Right Description */}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-14 md:mb-18 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-serif italic text-base text-secondary font-medium">02</span>
              <span className="h-px w-8 bg-secondary/40" />
              <span className="font-mono uppercase tracking-[0.2em] text-xs font-semibold text-muted-foreground">
                Core Consultancy Pillars
              </span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.12] tracking-tight text-foreground font-bold">
              Hands-on strategy built for{" "}
              <span className="italic font-light text-secondary">profitable occupancy.</span>
            </h2>
          </div>

          <div className="lg:col-span-5">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-lg lg:ml-auto">
              I replace guesswork with daily rate updates and direct extranet audits. We stop rate leakage and protect your take-home profit.
            </p>
          </div>
        </div>

        {/* 3-Column Pillar Cards */}
        <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-8 items-stretch">
          {pillars.map((pillar, i) => <article key={pillar.title} data-index={i} className={`group flex flex-col bg-card border border-border hover:border-secondary/60 transition-all duration-500 shadow-sm hover:shadow-md rounded-xl overflow-hidden ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
        }}>
              {/* Image Banner with Hover Scale */}
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted rounded-t-xl">
                <img src={pillar.image.src} data-wvc-srcset={pillar.image.srcset} data-wvc-sizes={pillar.image.sizes} alt={pillar.image.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
                
                {/* Floating Pillar Tag */}
                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest px-2.5 py-1 bg-background/90 text-foreground font-semibold backdrop-blur-xs border border-border/40">
                    {pillar.tag}
                  </span>
                  <span className="font-serif italic text-sm text-primary-foreground font-semibold px-2 py-0.5 bg-secondary text-secondary-foreground">
                    {pillar.number}
                  </span>
                </div>
              </div>

              {/* Card Content Area */}
              <div className="flex flex-col flex-1 p-6 md:p-8">
                <h3 className="font-serif text-xl md:text-2xl font-bold text-foreground mb-6 leading-snug group-hover:text-secondary transition-colors duration-300">
                  {pillar.title}
                </h3>

                {/* Gold-Bulleted List */}
                <ul className="space-y-3.5 mb-8 flex-1">
                  {pillar.points.map((point, pIndex) => <li key={pIndex} className="flex items-start gap-3 text-sm md:text-[0.9375rem] text-muted-foreground leading-relaxed">
                      <span className="mt-1 flex items-center justify-center size-4 shrink-0 bg-secondary/15 text-secondary border border-secondary/30 rounded-md">
                        <Check className="size-2.5 stroke-[3]" aria-hidden="true" />
                      </span>
                      <span>{point}</span>
                    </li>)}
                </ul>

                {/* Arrow Link */}
                <div className="pt-4 border-t border-border/70 mt-auto">
                  <Link to={pillar.link} className="inline-flex items-center gap-2 text-xs md:text-sm font-mono uppercase tracking-wider font-semibold text-foreground hover:text-secondary transition-colors duration-300 group/link">
                    <span>{pillar.linkText}</span>
                    <ArrowRight className="size-4 text-secondary transition-transform duration-300 group-hover/link:translate-x-1" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </article>)}
        </div>

        {/* Bottom Callout & Secondary CTA */}
        <div ref={ctaRef} className={`mt-14 md:mt-18 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6 transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="text-center sm:text-left">
            <p className="font-serif text-lg md:text-xl font-bold text-foreground">
              Need a clear revenue plan for your hotel?
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              I audit your live extranets and distribution costs with zero obligation.
            </p>
          </div>

          <Button asChild className="btn-primary shrink-0 font-mono text-xs uppercase tracking-wider h-11 px-6 font-semibold rounded-full">
            <Link to="/ContactPage#start-your-revenue-journey">
              Request a Free Revenue Audit
              <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}